import { StyleSheet, Dimensions } from "react-native";
import color from "@constants/color";
import { fontFamily, fontSize } from "@utils/styles/fonts";

const { width: SCREEN_WIDTH } = Dimensions.get("window");

// Semantic color aliases used throughout Onboarding screens
const textPrimary = color.Gray20; // #333333
const textMuted = color.color_808080; // closest to #8F92A1 — muted label/hint text
const borderDefault = color.color_D9D9D9; // #D9D9D9 ≈ #DDDDDD
const placeholderColor = color.disableFont; // rgba(189,189,189,1) ≈ #BCBCBC
const dividerColor = color.lineColor; // #F4F4F4
const shadowColor = color.black;

export const commonStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.white,
  },
  contentContainer: {
    paddingHorizontal: 20,
    flex: 1,
  },
  primaryButton: {
    backgroundColor: color.primary,
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: "center",
  },
  primaryButtonDisabled: {
    backgroundColor: color.lighterBlack,
  },
  primaryButtonText: {
    color: color.white,
  },
  // Floating label input container
  inputContainer: {
    borderWidth: 1,
    borderColor: borderDefault,
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 10,
    marginBottom: 16,
    backgroundColor: color.white,
    minHeight: 56,
    justifyContent: "center",
  },
  inputContainerFocused: {
    borderColor: color.primary,
  },
  inputLabel: {
    fontSize: 11,
    fontFamily: fontFamily.regular,
    color: color.primary,
    marginBottom: 2,
  },
  inputText: {
    color: textPrimary,
    padding: 0,
    margin: 0,
  },
  inputPlaceholder: {
    color: placeholderColor,
  },
});

export const aadhaarStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.white,
  },
  contentContainer: {
    paddingHorizontal: 24,
    paddingTop: 40,
    flex: 1,
  },
  title: {
    fontSize: 22,
    fontFamily: fontFamily.bold,
    color: textPrimary,
    marginBottom: 8,
  },
  subtitle: {
    color: textMuted,
    marginBottom: 32,
  },
  inputContainer: {
    borderWidth: 1,
    borderColor: borderDefault,
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 16,
    marginBottom: 24,
    backgroundColor: color.white,
  },
  inputContainerFocused: {
    borderColor: color.primary,
    borderWidth: 1.5,
  },
  input: {
    fontSize: fontSize.base,
    fontFamily: fontFamily.regular,
    color: textPrimary,
    padding: 0,
    letterSpacing: 1,
  },
  verifyButton: {
    backgroundColor: color.primary,
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: "center",
  },
  verifyButtonDisabled: {
    backgroundColor: color.lighterBlack,
  },
  verifyButtonText: {
    color: color.white,
  },
  errorText: {
    color: color.errorRed,
    marginTop: -16,
    marginBottom: 16,
    marginLeft: 4,
  },
});

export const kycStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.white,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 40,
  },
  iconWrapper: {
    width: 96,
    height: 96,
    borderRadius: 48,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 24,
  },
  iconWrapperSuccess: {
    backgroundColor: color.veryLightGreen, // rgba(0, 171, 89, 0.12) is closest to veryLightGreen
  },
  iconWrapperFailure: {
    backgroundColor: color.lightRed, // rgba(228, 0, 70, 0.10) ≈ lightRed (#FFE1E5)
  },
  iconWrapperInfo: {
    backgroundColor: color.veryLightSkyBlue, // rgba(0, 153, 202, 0.10) ≈ veryLightSkyBlue (#D1ECF3)
  },
  iconEmoji: {
    fontSize: 48,
  },
  statusTitle: {
    fontSize: 22,
    fontFamily: fontFamily.bold,
    color: textPrimary,
    textAlign: "center",
    marginBottom: 10,
  },
  statusTitleSuccess: {
    color: color.plain_green,
  },
  statusTitleFailure: {
    color: color.primary,
  },
  statusTitleInfo: {
    color: color.pacificBlue,
  },
  statusSubtitle: {
    color: textMuted,
    textAlign: "center",
    marginBottom: 32,
  },
  retryButton: {
    backgroundColor: color.primary,
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 48,
    alignItems: "center",
  },
  retryButtonText: {
    color: color.white,
  },
  completeKycMessage: {
    color: textMuted,
    textAlign: "center",
    marginBottom: 24,
  },
});

export const personalDetailsStyles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 108,
  },
  dateInputRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  calendarIconContainer: {
    padding: 4,
  },
  calendarIcon: {
    fontSize: 16,
    color: placeholderColor,
  },
  bottomBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: color.white,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderTopWidth: 1,
    borderTopColor: dividerColor,
    elevation: 12,
    shadowColor,
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
  },
});

export const vehicleStyles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 108,
  },
  sectionTitle: {
    color: textPrimary,
    marginBottom: 14,
    marginTop: 4,
  },
  vehicleTypeContainer: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 28,
  },
  vehicleTypeCard: {
    flex: 1,
    borderWidth: 1.5,
    borderColor: borderDefault,
    borderRadius: 12,
    paddingVertical: 18,
    paddingHorizontal: 4,
    alignItems: "center",
    backgroundColor: color.white,
  },
  vehicleTypeCardSelected: {
    borderColor: color.primary,
    borderWidth: 2,
    backgroundColor: color.veryLightOrange_rgb, // rgba(192, 110, 53, 0.05) — closest available tint of primary
  },
  vehicleTypeEmoji: {
    fontSize: 32,
    marginBottom: 8,
  },
  vehicleTypeText: {
    fontSize: 11,
    fontFamily: fontFamily.medium,
    color: textMuted,
    textAlign: "center",
  },
  vehicleTypeTextSelected: {
    color: color.primary,
    fontFamily: fontFamily.bold,
  },
  documentSection: {
    marginBottom: 20,
  },
  documentLabel: {
    fontSize: fontSize.sm,
    fontFamily: fontFamily.medium,
    color: textMuted,
    marginBottom: 10,
  },
  requiredStar: {
    color: color.primary,
  },
  uploadRow: {
    flexDirection: "row",
    gap: 12,
  },
  uploadBox: {
    flex: 1,
    borderRadius: 8,
    height: 100,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: color.Gray20,
    gap: 6,
    overflow: "hidden",
  },
  uploadBoxFilled: {
    backgroundColor: color.veryLightGreen,
    borderWidth: 1,
    borderColor: color.plain_green,
  },
  uploadBoxIcon: {
    fontSize: 20,
    color: color.white,
  },
  uploadCheckmark: {
    fontSize: 20,
    color: color.plain_green,
  },
  uploadEmoji: {
    fontSize: 24,
    color: color.white,
  },
  yourDocsSectionTitle: {
    marginTop: 4,
    marginBottom: 6,
  },
  yourDocsHelperText: {
    color: textMuted,
    marginBottom: 20,
  },
  uploadText: {
    fontSize: 11,
    fontFamily: fontFamily.regular,
    color: color.white,
  },
  uploadedFileName: {
    fontSize: 11,
    fontFamily: fontFamily.medium,
    color: color.plain_green,
    textAlign: "center",
    paddingHorizontal: 4,
  },
  uploadedFileSize: {
    color: textMuted,
  },
  photoUploadBox: {
    borderRadius: 8,
    height: 100,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 24,
    backgroundColor: color.Gray20,
    gap: 6,
    width: (SCREEN_WIDTH - 40) / 2,
    overflow: "hidden",
  },
  bottomBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: color.white,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderTopWidth: 1,
    borderTopColor: dividerColor,
    elevation: 12,
    shadowColor,
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
  },
});

export const hubStyles = StyleSheet.create({
  title: {
    color: textPrimary,
    marginBottom: 20,
    marginTop: 8,
  },
  dropdownContainer: {
    borderWidth: 1,
    borderColor: borderDefault,
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: color.white,
    minHeight: 52,
  },
  dropdownLabel: {
    fontSize: 11,
    fontFamily: fontFamily.regular,
    color: color.primary,
    marginBottom: 2,
  },
  dropdownValue: {
    color: textPrimary,
  },
  dropdownPlaceholder: {
    color: placeholderColor,
  },
  dropdownChevron: {
    fontSize: fontSize.xs,
    color: textMuted,
  },
  dropdownList: {
    borderWidth: 1,
    borderColor: borderDefault,
    borderRadius: 10,
    marginBottom: 16,
    marginTop: -8,
    backgroundColor: color.white,
    overflow: "hidden",
    elevation: 2,
    shadowColor,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 4,
  },
  dropdownListItem: {
    paddingHorizontal: 16,
    paddingVertical: 13,
    borderBottomWidth: 1,
    borderBottomColor: dividerColor,
  },
  dropdownListItemText: {
    color: textPrimary,
  },
  dropdownFlex: {
    flex: 1,
  },
  dropdownListItemLast: {
    borderBottomWidth: 0,
  },
  hubCardCheck: {
    fontSize: 16,
    color: color.primary,
  },
  hubScrollContent: {
    paddingHorizontal: 16,
    paddingTop: 20,
    paddingBottom: 100,
    backgroundColor: color.lightGray2,
    flexGrow: 1,
  },
  hubLoaderContainer: {
    alignItems: "center",
    marginTop: 40,
  },
  divider: {
    height: 1,
    backgroundColor: color.lightGray2,
    marginVertical: 16,
  },
  hubListTitle: {
    fontSize: 16,
    fontFamily: fontFamily.bold,
    color: textPrimary,
    marginBottom: 12,
  },
  hubCard: {
    borderRadius: 14,
    padding: 16,
    marginBottom: 10,
    backgroundColor: color.white,
    elevation: 1,
    shadowColor,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 4,
  },
  hubCardSelected: {
    borderWidth: 1.5,
    borderColor: color.primary,
  },
  hubCardHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  hubName: {
    fontSize: 15,
    fontFamily: fontFamily.bold,
    color: textPrimary,
    marginBottom: 4,
  },
  hubAddress: {
    fontSize: 13,
    fontFamily: fontFamily.regular,
    color: textMuted,
    marginBottom: 12,
    lineHeight: 18,
  },
  hubCardFooter: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  incentiveBadge: {
    backgroundColor: color.veryLightGreen,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    alignSelf: "flex-start",
  },
  incentiveText: {
    color: color.plain_green,
  },
  viewOnMapText: {
    fontSize: 13,
    fontFamily: fontFamily.medium,
    color: color.primary,
  },
  infoMessage: {
    fontSize: 13,
    fontFamily: fontFamily.regular,
    color: textMuted,
    textAlign: "center",
    marginTop: 24,
    paddingHorizontal: 16,
  },
  locationStatusContainer: {
    alignItems: "center",
    paddingTop: 60,
    paddingHorizontal: 24,
    gap: 16,
  },
  locationStatusText: {
    fontSize: 14,
    fontFamily: fontFamily.regular,
    color: textMuted,
    textAlign: "center",
  },
  locationDeniedTitle: {
    fontSize: 16,
    fontFamily: fontFamily.semibold ?? fontFamily.medium,
    color: textPrimary,
    textAlign: "center",
  },
  retryButton: {
    marginTop: 8,
    backgroundColor: color.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 10,
  },
  retryButtonText: {
    fontSize: 14,
    fontFamily: fontFamily.semibold ?? fontFamily.medium,
    color: color.white,
  },
  distanceBadge: {
    fontSize: 12,
    fontFamily: fontFamily.regular,
    color: textMuted,
  },
  continueButton: {
    borderRadius: 30,
  },
  bottomBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: color.lightGray2,
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
});

export const submittedStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.white,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 32,
  },
  iconWrapper: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: color.veryLightGreen,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 28,
  },
  iconEmoji: {
    fontSize: 52,
  },
  title: {
    color: textPrimary,
    textAlign: "center",
    marginBottom: 10,
  },
  subtitle: {
    color: textMuted,
    textAlign: "center",
    marginBottom: 32,
  },
  hubCard: {
    width: "100%",
    backgroundColor: color.white,
    borderRadius: 14,
    padding: 16,
    marginBottom: 36,
    borderWidth: 1,
    borderColor: color.mercury, // #E5E5E5 ≈ #EEEEEE
    elevation: 2,
    shadowColor,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
  },
  hubCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
    gap: 8,
  },
  locationIcon: {
    fontSize: 18,
  },
  selectedSpokeLabel: {
    fontSize: 11,
    fontFamily: fontFamily.regular,
    color: textMuted,
    marginBottom: 6,
  },
  hubName: {
    color: textPrimary,
    marginBottom: 4,
  },
  hubAddress: {
    color: textMuted,
    lineHeight: 18,
  },
  instructionText: {
    marginBottom: 32,
  },
  nextButton: {
    backgroundColor: color.primary,
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 64,
    alignItems: "center",
  },
  nextButtonText: {
    color: color.white,
  },
  kycStatusRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 28,
    gap: 8,
  },
  kycStatusLabel: {
    fontSize: 13,
    fontFamily: fontFamily.regular,
    color: textMuted,
  },
  kycStatusPill: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 20,
  },
  kycStatusPillText: {
    fontSize: 12,
    fontFamily: fontFamily.semibold ?? fontFamily.medium,
  },
});

export const riderTypeStyles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: color.white,
  },
  heroArea: {
    backgroundColor: color.white,
    paddingTop: 16,
    paddingHorizontal: 16,
    paddingBottom: 24,
    alignItems: "flex-start",
  },
  card: {
    backgroundColor: color.white,
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 32,
    flex: 1,
  },
  title: {
    color: textPrimary,
    marginBottom: 8,
  },
  subtitle: {
    color: textMuted,
    marginBottom: 28,
  },
  radioOption: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1.5,
    borderColor: borderDefault,
    borderRadius: 12,
    padding: 16,
    marginBottom: 14,
    backgroundColor: color.white,
  },
  radioOptionSelected: {
    borderColor: color.primary,
    backgroundColor: color.veryLightOrange_rgb,
  },
  radioCircle: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: borderDefault,
    marginRight: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  radioCircleSelected: {
    borderColor: color.primary,
  },
  radioCircleInner: {
    width: 11,
    height: 11,
    borderRadius: 6,
    backgroundColor: color.primary,
  },
  radioLabel: {
    color: textPrimary,
    flex: 1,
  },
  radioLabelSelected: {
    color: color.primary,
    fontFamily: fontFamily.bold,
  },
  continueButton: {
    backgroundColor: color.primary,
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: "center",
    marginTop: 12,
  },
  continueButtonDisabled: {
    backgroundColor: color.lighterBlack,
  },
  continueButtonText: {
    color: color.white,
  },
});

export const documentOwnershipStyles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 108,
  },
  title: {
    color: textPrimary,
    marginBottom: 24,
  },
  radioOption: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1.5,
    borderColor: borderDefault,
    borderRadius: 12,
    padding: 16,
    marginBottom: 14,
    backgroundColor: color.white,
  },
  radioOptionSelected: {
    borderColor: color.primary,
    backgroundColor: color.veryLightOrange_rgb,
  },
  radioCircle: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: borderDefault,
    marginRight: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  radioCircleSelected: {
    borderColor: color.primary,
  },
  radioCircleInner: {
    width: 11,
    height: 11,
    borderRadius: 6,
    backgroundColor: color.primary,
  },
  radioLabel: {
    color: textPrimary,
    flex: 1,
  },
  radioLabelSelected: {
    color: color.primary,
    fontFamily: fontFamily.bold,
  },
  bottomBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: color.white,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderTopWidth: 1,
    borderTopColor: dividerColor,
    elevation: 12,
    shadowColor,
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
  },
});

export const addressStyles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 108,
  },
  dropdownContainer: {
    borderWidth: 1,
    borderColor: borderDefault,
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: color.white,
    minHeight: 52,
  },
  dropdownLabel: {
    fontSize: 11,
    fontFamily: fontFamily.regular,
    color: color.primary,
    marginBottom: 2,
  },
  dropdownValue: {
    color: textPrimary,
  },
  dropdownPlaceholder: {
    color: placeholderColor,
  },
  dropdownChevron: {
    fontSize: fontSize.xs,
    color: textMuted,
  },
  dropdownList: {
    borderWidth: 1,
    borderColor: borderDefault,
    borderRadius: 10,
    marginBottom: 16,
    marginTop: -8,
    backgroundColor: color.white,
    overflow: "hidden",
    elevation: 2,
    shadowColor,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 4,
  },
  dropdownListItem: {
    paddingHorizontal: 16,
    paddingVertical: 13,
    borderBottomWidth: 1,
    borderBottomColor: dividerColor,
  },
  dropdownListItemText: {
    color: textPrimary,
  },
  dropdownFlex: {
    flex: 1,
  },
  dropdownListItemLast: {
    borderBottomWidth: 0,
  },
  bottomBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: color.white,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderTopWidth: 1,
    borderTopColor: dividerColor,
    elevation: 12,
    shadowColor,
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
  },
});

export const signUpSuccessfulStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.white,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 36,
  },
  iconWrapper: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: color.veryLightGreen,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 24,
  },
  iconEmoji: {
    fontSize: 52,
  },
  title: {
    color: color.plain_green,
    textAlign: "center",
    marginBottom: 12,
  },
  subtitle: {
    color: textMuted,
    textAlign: "center",
    lineHeight: 22,
    marginBottom: 40,
  },
  okayButton: {
    backgroundColor: color.primary,
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 64,
    alignItems: "center",
  },
  okayButtonText: {
    color: color.white,
  },
});

// Exported for use in components needing placeholder color
export { placeholderColor };
